import { AlertTriangle, Save, X } from 'lucide-react';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/catalyst/button';
import { Input } from '@/components/ui/catalyst/input';
import { Textarea } from '@/components/ui/catalyst/textarea';
import { DatePicker } from '@/components/ui/date-picker';
import { useUpdateIncidentMutation } from '@/services/api/incidentService';
import type { IncidentSchema } from '@/services/types/incident-types';

interface IncidentEditFormProps {
  matterId: string;
  incident: IncidentSchema;
  onSave: () => void;
  onCancel: () => void;
}

interface FormValues {
  description: string;
  date_of_loss: string | null;
  time_of_loss: string;
  incident_type: string;
  location: string;
  cross_street: string;
  weather_conditions: string;
  injuries_described: string;
  hospital: string;
  ambulance: string;
  current_treatment: string;
  police_report: string;
  witnesses: string;
  statements: string;
  photos: string;
  representation: string;
}

const FormField = ({
  label,
  children,
  hint,
}: {
  label: string;
  children: React.ReactNode;
  hint?: string;
}) => (
  <div className="space-y-0.5">
    <div className="text-xs text-gray-500">{label}</div>
    {children}
    {hint && <p className="text-xs text-gray-400">{hint}</p>}
  </div>
);

/**
 * Inline edit form for incident details
 */
export const IncidentEditForm = ({
  matterId,
  incident,
  onSave,
  onCancel,
}: IncidentEditFormProps) => {
  const [updateIncident, { isLoading }] = useUpdateIncidentMutation();

  // Track if location was modified (triggers re-normalization warning)
  const [locationModified, setLocationModified] = useState(false);

  // Initialize form values from incident
  const [values, setValues] = useState<FormValues>({
    description: incident.description || '',
    date_of_loss: incident.date_of_loss || null,
    time_of_loss: incident.time_of_loss || '',
    incident_type: incident.incident_type || '',
    location: incident.location || '',
    cross_street: incident.cross_street || '',
    weather_conditions: incident.weather_conditions || '',
    injuries_described: incident.injuries_described || '',
    hospital: incident.hospital || '',
    ambulance: incident.ambulance || '',
    current_treatment: incident.current_treatment || '',
    police_report: incident.police_report || '',
    witnesses: incident.witnesses || '',
    statements: incident.statements || '',
    photos: incident.photos || '',
    representation: incident.representation || '',
  });

  const handleChange = useCallback(
    (field: keyof FormValues, value: string | null) => {
      setValues((prev) => ({ ...prev, [field]: value }));

      // Track location modifications
      if (field === 'location' && value !== incident.location) {
        setLocationModified(true);
      }
    },
    [incident.location],
  );

  const handleSubmit = async () => {
    try {
      await updateIncident({
        matterId,
        incidentId: incident.id,
        incident: {
          id: incident.id,
          description: values.description || null,
          date_of_loss: values.date_of_loss,
          time_of_loss: values.time_of_loss || null,
          incident_type: values.incident_type || null,
          location: values.location || null,
          cross_street: values.cross_street || null,
          weather_conditions: values.weather_conditions || null,
          injuries_described: values.injuries_described || null,
          hospital: values.hospital || null,
          ambulance: values.ambulance || null,
          current_treatment: values.current_treatment || null,
          police_report: values.police_report || null,
          witnesses: values.witnesses || null,
          statements: values.statements || null,
          photos: values.photos || null,
          representation: values.representation || null,
        },
      }).unwrap();

      onSave();
    } catch (error) {
      toast.error('Failed to update accident details');
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
      <div className="px-3 py-2.5 border-b border-gray-100 flex items-center justify-between bg-gray-50">
        <h3 className="text-sm font-medium text-gray-900">
          Edit Accident Details
        </h3>
        <div className="flex items-center gap-1.5">
          <Button plain onClick={onCancel} disabled={isLoading}>
            <X className="h-3.5 w-3.5" />
          </Button>
          <Button color="primary" onClick={handleSubmit} disabled={isLoading}>
            <Save className="h-3.5 w-3.5" />
            {isLoading ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>

      <div className="p-3 space-y-3">
        <div className="grid grid-cols-3 gap-2">
          <FormField label="Date">
            <DatePicker
              date={values.date_of_loss}
              onChange={(date) =>
                handleChange(
                  'date_of_loss',
                  date ? date.toISOString().split('T')[0] : null,
                )
              }
              placeholder="Select date"
              className="w-full"
            />
          </FormField>
          <FormField label="Time">
            <Input
              type="text"
              value={values.time_of_loss}
              onChange={(e) => handleChange('time_of_loss', e.target.value)}
              placeholder="2:30 PM"
            />
          </FormField>
          <FormField label="Type">
            <Input
              type="text"
              value={values.incident_type}
              onChange={(e) => handleChange('incident_type', e.target.value)}
              placeholder="MVA"
            />
          </FormField>
        </div>

        <FormField
          label="Location"
          hint="Triggers address verification on save"
        >
          <Textarea
            value={values.location}
            onChange={(e) => handleChange('location', e.target.value)}
            placeholder="Intersection of Main St and Oak Ave, Springfield, IL"
            rows={2}
          />
        </FormField>

        {locationModified && (
          <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            Location will be re-verified after saving
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          <FormField label="Cross Street">
            <Input
              type="text"
              value={values.cross_street}
              onChange={(e) => handleChange('cross_street', e.target.value)}
              placeholder="Oak Avenue"
            />
          </FormField>
          <FormField label="Weather">
            <Input
              type="text"
              value={values.weather_conditions}
              onChange={(e) =>
                handleChange('weather_conditions', e.target.value)
              }
              placeholder="Wet roadway, raining"
            />
          </FormField>
        </div>

        <FormField label="Description">
          <Textarea
            value={values.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Describe what happened..."
            rows={3}
          />
        </FormField>

        <FormField label="Injuries">
          <Textarea
            value={values.injuries_described}
            onChange={(e) => handleChange('injuries_described', e.target.value)}
            placeholder="Describe injuries sustained..."
            rows={2}
          />
        </FormField>

        <div className="grid grid-cols-1 gap-2">
          <FormField label="Hospital">
            <Input
              type="text"
              value={values.hospital}
              onChange={(e) => handleChange('hospital', e.target.value)}
              placeholder="Transported to Community Medical Center"
            />
          </FormField>
          <FormField label="Ambulance">
            <Input
              type="text"
              value={values.ambulance}
              onChange={(e) => handleChange('ambulance', e.target.value)}
              placeholder="EMS responded and transported"
            />
          </FormField>
          <FormField label="Current Treatment">
            <Input
              type="text"
              value={values.current_treatment}
              onChange={(e) =>
                handleChange('current_treatment', e.target.value)
              }
              placeholder="PT 3x/week, medication"
            />
          </FormField>
        </div>

        <div className="grid grid-cols-1 gap-2">
          <FormField label="Police Report">
            <Input
              type="text"
              value={values.police_report}
              onChange={(e) => handleChange('police_report', e.target.value)}
              placeholder="Case #25-25579, Toms River PD"
            />
          </FormField>
          <FormField label="Witnesses">
            <Input
              type="text"
              value={values.witnesses}
              onChange={(e) => handleChange('witnesses', e.target.value)}
              placeholder="2 witnesses identified"
            />
          </FormField>
          <FormField label="Statements">
            <Input
              type="text"
              value={values.statements}
              onChange={(e) => handleChange('statements', e.target.value)}
              placeholder="Driver was stopped at red light"
            />
          </FormField>
          <FormField label="Photos/Evidence">
            <Input
              type="text"
              value={values.photos}
              onChange={(e) => handleChange('photos', e.target.value)}
              placeholder="Multiple photographs of vehicle damage"
            />
          </FormField>
          <FormField label="Prior Representation">
            <Input
              type="text"
              value={values.representation}
              onChange={(e) => handleChange('representation', e.target.value)}
              placeholder="None"
            />
          </FormField>
        </div>
      </div>
    </div>
  );
};
